import type { Data as DndKitData } from '@dnd-kit/core';

import { Folder, RepositoryIssue } from '@qality/common-model';

/**
 * In react-dnd, the data transfer via 'data' property is not type-safe.
 * This utility provides a way to make it type-safe.
 * It composes and decomposes the data to/from the 'data' property.
 * It also provides type guards to check the type of the data.
 *
 * Usage:
 * - Compose the data for useDraggable/useDroppable hook, eg:
 *  const data = DataUtils.composeData({ type: 'issue', payload: { issue } });
 *
 * - Decompose the data in dragStart/dragEnd event handlers, eg:
 *  const { issue } = DataUtils.decomposeData(data);
 *
 * - Check the type of the data to narrow down types for TypeScript, eg:
 *  if (DataUtils.isDraggableIssueData(data)) {
 *    // now the typeof data is not a genreic Data, but specified DraggableIssueData
 *  }
 *
 * - Identify the action in dragStart/dragEnd event handlers, eg:
 *  const { action, activeData } = DataUtils.identifyActionStart(event.active);
 *
 * It's easy to extend the data types by adding new types to the Data or Actions* union type,
 * and adding new type guards.
 */

export type DraggableIssueData = {
  type: 'issue';
  payload: { issue: RepositoryIssue };
};

export type DroppableFolderData = {
  type: 'folder';
  payload: { folder: Folder; isDisabled: boolean };
};

export type Data = DraggableIssueData | DroppableFolderData;

export const composeData = (data: Data): Data => {
  return data;
};

export const decomposeData = (
  value: DndKitData | null
): DraggableIssueData | DroppableFolderData => {
  if (!value) {
    throw new Error(`Invalid value: ${value}`);
  }

  if (value.current.type === 'issue') {
    return {
      type: 'issue',
      payload: { issue: value.current.payload.issue },
    };
  }

  if (value.current.type === 'folder') {
    const { folder, isDisabled } = value.current.payload;
    return {
      type: 'folder',
      payload: { folder, isDisabled },
    };
  }

  throw new Error(`Invalid type: ${value.current.type}`);
};

export type ActionsStart =
  | { action: 'invalid'; activeData?: never }
  | { action: 'drag-issue'; activeData: DraggableIssueData };

export type ActionsEnd =
  | { action: 'invalid'; activeData?: never; overData?: never }
  | {
      action: 'dnd-issue-to-folder';
      activeData: DraggableIssueData;
      overData: DroppableFolderData;
    };

export const identifyActionStart = (
  active: DndKitData | null
): ActionsStart => {
  if (!active) {
    return { action: 'invalid' };
  }

  const activeData = decomposeData(active.data);

  const isDraggingIssue = isDraggableIssueData(activeData);
  if (isDraggingIssue) {
    return { action: 'drag-issue', activeData };
  }

  return { action: 'invalid' };
};

export const identifyActionEnd = (
  active: DndKitData | null,
  over: DndKitData | null
): ActionsEnd => {
  if (!active || !over) {
    return { action: 'invalid' };
  }

  const activeData = decomposeData(active.data);
  const overData = decomposeData(over.data);

  const isSameTypeAndId =
    activeData.type === overData.type && active.id === over.id;
  if (isSameTypeAndId) {
    return { action: 'invalid' };
  }

  const isDraggingIssueToFolder =
    isDraggableIssueData(activeData) && isDroppableFolderData(overData);
  if (isDraggingIssueToFolder) {
    return { action: 'dnd-issue-to-folder', activeData, overData };
  }

  return { action: 'invalid' };
};

export const isDraggableIssueData = (
  value: Data
): value is DraggableIssueData => {
  return value.type === 'issue';
};

export const isDroppableFolderData = (
  value: Data
): value is DroppableFolderData => {
  return value.type === 'folder';
};

export const DataUtils = {
  composeData,
  decomposeData,
  identifyActionStart,
  identifyActionEnd,
  isDraggableIssueData,
  isDroppableFolderData,
};
